import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Truck, User, Plus, PencilSimple, Trash, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { VehiculoDialog } from '@/components/VehiculoDialog';
import { ConductorDialog } from '@/components/ConductorDialog';
import { LoadingOrEmpty } from '@/components/LoadingOrEmpty';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tipoLabels = {
  Van: 'Van', Camioneta: 'Camioneta', Auto: 'Auto',
  Ambulancia: 'Ambulancia', Camion: 'Camión', Otro: 'Otro'
};

const estadoVehiculoConfig = {
  activo: { label: 'Activo', color: '#0A6E3F', bg: '#0A6E3F20' },
  mantenimiento: { label: 'Mantenimiento', color: '#F5A623', bg: '#F5A62320' },
  baja: { label: 'De baja', color: '#6C757D', bg: '#6C757D20' }
};

const estadoConductorConfig = {
  activo: { label: 'Activo', color: '#0A6E3F', bg: '#0A6E3F20' },
  inactivo: { label: 'Inactivo', color: '#6C757D', bg: '#6C757D20' }
};

export const FlotaPage = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehiculos');
  const [showVehDialog, setShowVehDialog] = useState(false);
  const [showCondDialog, setShowCondDialog] = useState(false);
  const [editVehiculo, setEditVehiculo] = useState(null);
  const [editConductor, setEditConductor] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/vehiculos`, { withCredentials: true }),
        axios.get(`${API_URL}/api/conductores`, { withCredentials: true }),
      ]);
      setVehiculos(vRes.data);
      setConductores(cRes.data);
    } catch (e) {
      toast.error('Error al cargar la flota');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNewVehiculo = () => { setEditVehiculo(null); setShowVehDialog(true); };
  const openEditVehiculo = (v) => { setEditVehiculo(v); setShowVehDialog(true); };
  const openNewConductor = () => { setEditConductor(null); setShowCondDialog(true); };
  const openEditConductor = (c) => { setEditConductor(c); setShowCondDialog(true); };

  const removeVehiculo = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/api/vehiculos/${id}`, { withCredentials: true });
      toast.success('Vehículo eliminado');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const removeConductor = async (id) => {
    if (!window.confirm('¿Eliminar este conductor? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/api/conductores/${id}`, { withCredentials: true });
      toast.success('Conductor eliminado');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A] mb-2">
            Gestión de Flota
          </h1>
          <p className="text-[#495057] font-['IBM_Plex_Sans']">
            Administra vehículos y conductores disponibles para asignar a movilizaciones
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-white border border-[#E9ECEF] p-1 h-auto inline-flex">
              <TabsTrigger
                value="vehiculos"
                data-testid="tab-vehiculos"
                className="gap-2 font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#C1121F] data-[state=active]:text-white h-auto py-2.5 px-5"
              >
                <Truck size={16} weight="bold" /> Vehículos ({vehiculos.length})
              </TabsTrigger>
              <TabsTrigger
                value="conductores"
                data-testid="tab-conductores"
                className="gap-2 font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#C1121F] data-[state=active]:text-white h-auto py-2.5 px-5"
              >
                <User size={16} weight="bold" /> Conductores ({conductores.length})
              </TabsTrigger>
            </TabsList>

            {activeTab === 'vehiculos' && (
              <Button
                data-testid="add-vehiculo-button"
                onClick={openNewVehiculo}
                className="gap-2 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] h-12 px-5"
              >
                <Plus size={18} weight="bold" /> Nuevo Vehículo
              </Button>
            )}
            {activeTab === 'conductores' && (
              <Button
                data-testid="add-conductor-button"
                onClick={openNewConductor}
                className="gap-2 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] h-12 px-5"
              >
                <Plus size={18} weight="bold" /> Nuevo Conductor
              </Button>
            )}
          </div>

          <TabsContent value="vehiculos">
            <LoadingOrEmpty
              loading={loading}
              isEmpty={vehiculos.length === 0}
              emptyIcon={<Truck size={48} className="text-[#E9ECEF] mx-auto mb-4" weight="bold" />}
              emptyMessage="No hay vehículos registrados. Agrega el primero para comenzar."
              testId="vehiculos"
            >
              <div className="bg-white border border-[#E9ECEF] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E9ECEF] bg-[#F8F9FA]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Patente</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Alias</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Marca / Modelo</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Tipo</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Capacidad</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Estado</th>
                      <th className="text-right p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehiculos.map((v) => {
                      const st = estadoVehiculoConfig[v.estado] || estadoVehiculoConfig.activo;
                      return (
                        <tr key={v.id} data-testid={`vehiculo-row-${v.id}`} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]">
                          <td className="p-3 font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{v.patente}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#0A0A0A]">{v.alias || '-'}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#495057]">{v.marca_modelo || '-'}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#495057]">{tipoLabels[v.tipo] || v.tipo}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#495057]">{v.capacidad}</td>
                          <td className="p-3">
                            <span
                              className="px-2 py-0.5 text-xs font-bold font-['IBM_Plex_Sans']"
                              style={{ backgroundColor: st.bg, color: st.color }}
                            >
                              {st.label}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                data-testid={`edit-vehiculo-${v.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openEditVehiculo(v)}
                                className="h-8 w-8 p-0 border-[#E9ECEF] hover:bg-[#F8F9FA]"
                              >
                                <PencilSimple size={14} weight="bold" />
                              </Button>
                              <Button
                                data-testid={`delete-vehiculo-${v.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => removeVehiculo(v.id)}
                                className="h-8 w-8 p-0 border-[#E9ECEF] hover:bg-[#FF3B30] hover:text-white hover:border-[#FF3B30]"
                              >
                                <Trash size={14} weight="bold" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </LoadingOrEmpty>
          </TabsContent>

          <TabsContent value="conductores">
            <LoadingOrEmpty
              loading={loading}
              isEmpty={conductores.length === 0}
              emptyIcon={<User size={48} className="text-[#E9ECEF] mx-auto mb-4" weight="bold" />}
              emptyMessage="No hay conductores registrados. Agrega el primero para comenzar."
              testId="conductores"
            >
              <div className="bg-white border border-[#E9ECEF] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E9ECEF] bg-[#F8F9FA]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Nombre</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Cédula</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Teléfono</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Licencia</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Estado</th>
                      <th className="text-right p-3 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans']">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conductores.map((c) => {
                      const st = estadoConductorConfig[c.estado] || estadoConductorConfig.activo;
                      return (
                        <tr key={c.id} data-testid={`conductor-row-${c.id}`} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]">
                          <td className="p-3 font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{c.nombre_completo}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#495057]">{c.cedula || '-'}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#0A0A0A]">{c.telefono}</td>
                          <td className="p-3 font-['IBM_Plex_Sans'] text-[#495057]">{c.licencia || '-'}</td>
                          <td className="p-3">
                            <span
                              className="px-2 py-0.5 text-xs font-bold font-['IBM_Plex_Sans']"
                              style={{ backgroundColor: st.bg, color: st.color }}
                            >
                              {st.label}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                data-testid={`edit-conductor-${c.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openEditConductor(c)}
                                className="h-8 w-8 p-0 border-[#E9ECEF] hover:bg-[#F8F9FA]"
                              >
                                <PencilSimple size={14} weight="bold" />
                              </Button>
                              <Button
                                data-testid={`delete-conductor-${c.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => removeConductor(c.id)}
                                className="h-8 w-8 p-0 border-[#E9ECEF] hover:bg-[#FF3B30] hover:text-white hover:border-[#FF3B30]"
                              >
                                <Trash size={14} weight="bold" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </LoadingOrEmpty>
          </TabsContent>
        </Tabs>
      </div>

      <VehiculoDialog
        open={showVehDialog}
        onOpenChange={setShowVehDialog}
        vehiculo={editVehiculo}
        onSuccess={fetchAll}
      />
      <ConductorDialog
        open={showCondDialog}
        onOpenChange={setShowCondDialog}
        conductor={editConductor}
        onSuccess={fetchAll}
      />
    </div>
  );
};
